import { useRef, useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, connect } from "react-redux";
import {
  getInAppNotifications,
  markAllNotificationsRead,
  resetInAppNotifications,
} from "../../store/actions/in-app-notification";
import { GetInAppNotificationsInDto } from "../../types/in-app-notification.dto";
import { useWindowSize } from "../../hooks/useWindowSize";
import Sidebar from "../Sidebar";
import NotificationItem from "./components/NotificationItem";
import Icon from "../Icon";
import notificationIcon from "../../assets/sessions/notification-icon.svg";
import {
  PanelWrapper,
  PanelHeader,
  PanelTitle,
  MarkAllButton,
  PanelBody,
  EmptyState,
  EmptyText,
  LoadingWrapper,
} from "./styles";

interface ComponentProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLButtonElement>;
  notifications: GetInAppNotificationsInDto | null;
  notificationsStates: {
    loading: boolean;
    error: boolean | string;
    success: boolean;
  };
  unreadCount: number;
}

const NotificationPanel = ({
  isOpen,
  onClose,
  anchorRef,
  notifications,
  notificationsStates,
  unreadCount,
}: ComponentProps) => {
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const panelRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      dispatch(resetInAppNotifications());
      dispatch(getInAppNotifications({ page: 1, perPage: 15 }));
    }
  }, [isOpen]);

  // Calculate position from anchor button, opening below and right-aligned
  useEffect(() => {
    if (!isOpen || isMobile || !anchorRef?.current) return;

    const rect = anchorRef.current.getBoundingClientRect();
    const panelWidth = 380;
    const margin = 16;
    const top = rect.bottom + 8;
    // Right-align panel with button's right edge, clamped to viewport
    const left = Math.max(margin, rect.right - panelWidth);

    setPanelPosition({ top, left });
  }, [isOpen, isMobile, anchorRef]);

  // Click outside to close (desktop only)
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (panelRef.current && !panelRef.current.contains(target)) {
        // Also check if click was on the anchor button (toggle handled by parent)
        if (anchorRef?.current && anchorRef.current.contains(target)) return;
        onClose();
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen, isMobile, onClose, anchorRef]);

  const handleMarkAllAsRead = useCallback(() => {
    dispatch(markAllNotificationsRead());
  }, [dispatch]);

  const handleLoadMore = useCallback(() => {
    if (notificationsStates.loading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    dispatch(getInAppNotifications({ page: nextPage, perPage: 15, append: true }));
  }, [page, notificationsStates.loading, dispatch]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 50;

      if (!nearBottom || notificationsStates.loading) return;

      const hasMore =
        notifications?.pagination &&
        notifications.pagination.currentPage < notifications.pagination.lastPage;

      if (hasMore) {
        handleLoadMore();
      }
    },
    [notifications, notificationsStates.loading, handleLoadMore]
  );

  const items = notifications?.data || [];

  const panelContent = (
    <>
      <PanelHeader>
        <PanelTitle>Notificaciones</PanelTitle>
        <MarkAllButton
          onClick={handleMarkAllAsRead}
          disabled={unreadCount === 0}
        >
          Marcar todo como leído
        </MarkAllButton>
      </PanelHeader>

      <PanelBody onScroll={handleScroll}>
        {notificationsStates.loading && items.length === 0 ? (
          <LoadingWrapper>
            <span>Cargando...</span>
          </LoadingWrapper>
        ) : items.length === 0 ? (
          <EmptyState>
            <Icon
              icon={notificationIcon}
              size="32px"
              color="rgba(45, 52, 54, 0.2)"
            />
            <EmptyText>No tienes notificaciones</EmptyText>
          </EmptyState>
        ) : (
          <>
            {items.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClose={onClose}
              />
            ))}
            {notificationsStates.loading && (
              <LoadingWrapper>
                <span>Cargando...</span>
              </LoadingWrapper>
            )}
          </>
        )}
      </PanelBody>
    </>
  );

  if (!isOpen) return null;

  if (isMobile) {
    return (
      <Sidebar
        position="right"
        visible={isOpen}
        onClose={onClose}
        closeType="back"
        width="100%"
        title="Notificaciones"
      >
        <PanelBody onScroll={handleScroll} style={{ height: "calc(100vh - 80px)" }}>
          {notificationsStates.loading && items.length === 0 ? (
            <LoadingWrapper>
              <span>Cargando...</span>
            </LoadingWrapper>
          ) : items.length === 0 ? (
            <EmptyState>
              <Icon
                icon={notificationIcon}
                size="32px"
                color="rgba(45, 52, 54, 0.2)"
              />
              <EmptyText>No tienes notificaciones</EmptyText>
            </EmptyState>
          ) : (
            <>
              <div style={{ padding: "8px 16px" }}>
                <MarkAllButton
                  onClick={handleMarkAllAsRead}
                  disabled={unreadCount === 0}
                >
                  Marcar todo como leído
                </MarkAllButton>
              </div>
              {items.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClose={onClose}
                />
              ))}
              {notificationsStates.loading && (
                <LoadingWrapper>
                  <span>Cargando...</span>
                </LoadingWrapper>
              )}
            </>
          )}
        </PanelBody>
      </Sidebar>
    );
  }

  // Desktop: render as portal to escape sidebar stacking context
  return ReactDOM.createPortal(
    <PanelWrapper
      ref={panelRef}
      style={{ top: panelPosition.top, left: panelPosition.left }}
    >
      {panelContent}
    </PanelWrapper>,
    document.body
  );
};

const stateToProps = ({ inAppNotificationStore }) => {
  const { data: notifications, states: notificationsStates } =
    inAppNotificationStore.notifications;
  const { data: unreadCount } = inAppNotificationStore.unreadCount;

  return {
    notifications,
    notificationsStates,
    unreadCount,
  };
};

export default connect(stateToProps)(NotificationPanel);
